import React, { useEffect, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import Title from '../../component/Title'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import BottomButtonGroup from '../../component/BottomButtonGroup'
import { FORM_PANEL_SX, getFormChoiceCardSx } from '../../component/formSurfaceStyles'
import { createListLinkTarget, getLocationState } from '../../utils/listPageState'
import {
    applyAppMarketingDraft,
    clearAppMarketingDraft,
    createAppMarketingDraftPayload,
    NEW_APP_MARKETING_DRAFT_KEY,
    readAppMarketingDraft,
    saveAppMarketingDraft,
} from './appMarketingDraftUtils'

const PANEL_SX = FORM_PANEL_SX

const getChoiceCardSx = getFormChoiceCardSx

const EMAIL_SEND_OPTIONS = [
    {
        value: 0,
        title: '미발송',
        description: '메일 발송 상태를 미발송으로 저장합니다.',
    },
    {
        value: 1,
        title: '발송',
        description: '메일 발송 상태를 발송으로 저장합니다.',
    },
]

// 주요 입력 영역을 카드 단위로 묶어 등록/수정 화면의 구조를 맞춘다.
function FormSection({ title, description, children }) {
    return (
        <Paper variant="outlined" sx={PANEL_SX}>
            <Box sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {description}
                </Typography>
            </Box>
            {children}
        </Paper>
    )
}

// 라디오 옵션에 설명을 함께 표시해 선택 결과를 운영자가 바로 이해할 수 있게 한다.
function ChoiceCardLabel({ title, description }) {
    return (
        <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                {title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
                {description}
            </Typography>
        </Box>
    )
}

function EmailSendSetting({ value, disabled, onChange }) {
    return (
        <FormControl fullWidth>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                메일발송여부
            </Typography>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                    gap: 1,
                    mt: 1,
                }}
            >
                {EMAIL_SEND_OPTIONS.map((option) => {
                    const checked = Number(value ?? 0) === option.value

                    return (
                        <FormControlLabel
                            key={option.value}
                            value={option.value}
                            control={<Radio checked={checked} onChange={() => onChange(option.value)} disabled={disabled} />}
                            disabled={disabled}
                            sx={getChoiceCardSx(checked)}
                            label={(
                                <ChoiceCardLabel
                                    title={option.title}
                                    description={option.description}
                                />
                            )}
                        />
                    )
                })}
            </Box>
        </FormControl>
    )
}

export default function NewAppMarketing() {
    const history = useHistory()
    const location = useLocation()
    const locationState = getLocationState(location)
    const curPage = locationState.curPage || 1
    const searchText = locationState.searchText || ''
    const searchSelect = locationState.searchSelect || { searchStatus: '' }
    const listSearch = location.search || ''
    const [saving, setSaving] = useState(false)
    const [params, setParams] = useState({
        companyName: '',
        email: '',
        memo: '',
        isEmailSend: 0,
    })
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('info')
    const [messageSnackbar, setMessageSnackbar] = useState('')
    const [draftReady, setDraftReady] = useState(false)
    const initialDraftSnapshotRef = useRef('')

    useEffect(() => {
        const baseParams = {
            companyName: '',
            email: '',
            memo: '',
            isEmailSend: 0,
        }
        const storedDraft = readAppMarketingDraft(NEW_APP_MARKETING_DRAFT_KEY)
        const restoredDraft = applyAppMarketingDraft({
            baseParams,
            draft: storedDraft,
        })

        initialDraftSnapshotRef.current = JSON.stringify(createAppMarketingDraftPayload({
            params: baseParams,
        }))

        setParams((prevParams) => ({
            ...prevParams,
            ...restoredDraft.params,
        }))
        setDraftReady(true)
    }, [])

    useEffect(() => {
        if (!draftReady) {
            return
        }

        // 마케팅 작업 기업 등록은 목록 query를 건드리지 않고 폼 draft만 sessionStorage에 보존한다.
        const nextDraft = createAppMarketingDraftPayload({
            params,
        })
        const nextDraftSnapshot = JSON.stringify(nextDraft)

        if (nextDraftSnapshot === initialDraftSnapshotRef.current) {
            clearAppMarketingDraft(NEW_APP_MARKETING_DRAFT_KEY)
            return
        }

        saveAppMarketingDraft(NEW_APP_MARKETING_DRAFT_KEY, nextDraft)
    }, [draftReady, params])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const moveToList = (snackbarState) => {
        clearAppMarketingDraft(NEW_APP_MARKETING_DRAFT_KEY)
        history.push(createListLinkTarget({
            pathname: `/appMarketings`,
            search: listSearch,
            ...(snackbarState ? {
                openSnackbar: true,
                severitySnackbar: snackbarState.severity,
                messageSnackbar: snackbarState.message,
            } : {}),
            curPage: curPage,
            searchText: searchText,
            searchSelect: searchSelect
        }))
    }

    const handleUpdateField = (e) => {
        const { name, value } = e.target

        setParams((prevParams) => ({
            ...prevParams,
            [name]: value
        }))
    }

    const handleEmailSendChange = (isEmailSend) => {
        setParams((prevParams) => ({
            ...prevParams,
            isEmailSend,
        }))
    }

    const createData = async (nextParams) => {
        try {
            setSaving(true)
            const { data } = await API.post('/api/v1/admin/appMarketing/create', nextParams)

            if (data.code === 200) {
                // 등록 후에도 이전 마케팅 작업 기업 목록 조건으로 그대로 복귀시킨다.
                moveToList({
                    severity: 'success',
                    message: data.message,
                })
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('마케팅 작업 기업을 등록하지 못했습니다.')
        } finally {
            setSaving(false)
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault()

        if (saving) {
            return
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        const nextParams = {
            ...params,
            companyName: params.companyName.trim(),
            email: params.email.trim(),
        }

        setParams((prevParams) => ({
            ...prevParams,
            companyName: nextParams.companyName,
            email: nextParams.email,
        }))

        if (!nextParams.companyName) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('기업명을 입력하세요.')
            return
        }

        if (!nextParams.email) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('이메일을 입력하세요.')
            return
        }

        if (!emailRegex.test(nextParams.email)) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('올바른 이메일 형식을 입력하세요.')
            return
        }

        createData(nextParams)
    }
    
    return (
        <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Title>마케팅 작업 기업 등록</Title>
                <Box component="form" encType="multipart/form-data" onSubmit={handleSubmit} sx={{ opacity: saving ? 0.6 : 1 }}>
                    <Grid container spacing={2} sx={{ marginTop: 2 }}>
                        <Grid item xs={12}>
                            <FormSection
                                title="기업 정보"
                                description="마케팅 작업 기업명, 연락 이메일, 내부 메모를 작성합니다."
                            >
                                <Grid container spacing={1.5}>
                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth>
                                            <TextField
                                                label="기업명"
                                                required
                                                id="companyName"
                                                name="companyName"
                                                value={params.companyName}
                                                onChange={handleUpdateField}
                                                disabled={saving}
                                                size="small"
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth>
                                            <TextField
                                                label="이메일"
                                                required
                                                id="email"
                                                name="email"
                                                value={params.email}
                                                onChange={handleUpdateField}
                                                disabled={saving}
                                                size="small"
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormControl fullWidth>
                                            <TextField
                                                label="메모"
                                                id="memo"
                                                name="memo"
                                                multiline
                                                rows={10}
                                                value={params.memo || ''}
                                                onChange={handleUpdateField}
                                                disabled={saving}
                                                size="small"
                                            />
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </FormSection>
                        </Grid>
                        <Grid item xs={12}>
                            <FormSection
                                title="메일 발송 설정"
                                description="마케팅 작업 기업의 메일 발송 상태를 설정합니다."
                            >
                                <EmailSendSetting
                                    value={params.isEmailSend}
                                    disabled={saving}
                                    onChange={handleEmailSendChange}
                                />
                            </FormSection>
                        </Grid>
                    </Grid>
                    <BottomButtonGroup path="/appMarketings" search={listSearch} visible={true} curPage={curPage} searchText={searchText} searchSelect={searchSelect} onBackClick={() => clearAppMarketingDraft(NEW_APP_MARKETING_DRAFT_KEY)} submitDisabled={saving} submitLabel={saving ? '저장 중...' : '저장'} />
                </Box>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Paper>
        </Grid>
    )
}
